import type { ReactElement } from "react";

import type { BookExposureView, FundingPlanView } from "../api/client.js";

/**
 * The accountant: what each account should hold and how to get it there.
 *
 * A book's target is its busiest staking day in the last four weeks (or its
 * floor, for a new account), so it can take a repeat of that day without a
 * top-up. A limited book's target is zero. Moves are withdraw-then-deposit:
 * books cannot pay each other, so each move is two steps and the withdrawal
 * can take a few days to clear.
 */
const money = (cents: number): string =>
  `${cents < 0 ? "-" : ""}$${(Math.abs(cents) / 100).toFixed(0)}`;

function position(book: BookExposureView): {
  label: string;
  tone: "warn" | "positive" | undefined;
} {
  const target = book.targetCents ?? 0;
  // Open stakes count as still in the book (back at face value); only cash
  // can be withdrawn.
  const gap = book.availableCents + book.pendingCents - target;
  if (book.status !== "ACTIVE")
    return book.availableCents > 0
      ? { label: `withdraw ${money(book.availableCents)}`, tone: "positive" }
      : { label: "empty", tone: undefined };
  if (gap < 0) return { label: `short ${money(-gap)}`, tone: "warn" };
  if (book.availableCents + book.pendingCents > target * 1.25)
    return {
      label: `spare ${money(Math.min(gap, book.availableCents))}`,
      tone: "positive",
    };
  return { label: "on target", tone: undefined };
}

export function BookAccountant({
  books,
  funding,
}: {
  readonly books: readonly BookExposureView[];
  readonly funding: FundingPlanView;
}): ReactElement {
  const rows = books.toSorted(
    (a, b) => (b.peakDailyStakeCents ?? 0) - (a.peakDailyStakeCents ?? 0),
  );
  return (
    <section className="card" aria-label="Accountant">
      <div className="section-heading">
        <h3>Accountant</h3>
        <span>target = busiest day, or its share of a big night</span>
      </div>
      {funding.moves.length === 0 && funding.shortfallCents === 0 ? (
        <p>Every account can cover its busiest day. Nothing to move.</p>
      ) : (
        <ol aria-label="Money to move">
          {funding.moves.map((move) => (
            <li key={`${move.from}-${move.to}`}>
              Withdraw <strong>{money(move.amountCents)}</strong> from{" "}
              <strong>{move.from}</strong>, deposit it into{" "}
              <strong>{move.to}</strong>
            </li>
          ))}
          {funding.shortfallCents > 0 ? (
            <li>
              Deposit <strong>{money(funding.shortfallCents)}</strong> of new
              money: no account can spare enough to cover it.
            </li>
          ) : null}
        </ol>
      )}
      <div className="table-shell">
        <table className="data-table">
          <thead>
            <tr>
              <th scope="col">Book</th>
              <th scope="col">Avg / day</th>
              <th scope="col">Busiest day</th>
              <th scope="col">Target</th>
              <th scope="col">Available</th>
              <th scope="col">Position</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((book) => {
              const state = position(book);
              return (
                <tr
                  key={book.bookmaker}
                  className={book.status !== "ACTIVE" ? "row-muted" : undefined}
                >
                  <th scope="row">{book.bookmaker}</th>
                  <td data-label="Avg / day">
                    {money(book.avgDailyStakeCents ?? 0)}
                  </td>
                  <td data-label="Busiest day">
                    {money(book.peakDailyStakeCents ?? 0)}
                    {book.peakDay ? (
                      <span className="muted"> · {book.peakDay}</span>
                    ) : null}
                  </td>
                  <td data-label="Target">{money(book.targetCents ?? 0)}</td>
                  <td data-label="Available">
                    {money(book.availableCents)}
                    {book.pendingCents > 0
                      ? ` + ${money(book.pendingCents)} in bets`
                      : ""}
                  </td>
                  <td data-label="Position" className={state.tone}>
                    {state.label}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
